import { Link } from "react-router-dom";
import { PageShell, Section } from "@/components/PageShell";
import { SeoCta, SeoFaq, SeoRelated } from "@/components/SeoBits";
import { SEO_FAQS } from "@/lib/seoPages";

const a = "font-semibold text-[#7FC8A9] underline decoration-[#7FC8A9]/40 underline-offset-2 hover:text-white";

export default function AdhdDailyPlanner() {
  return (
    <PageShell
      testid="page-adhd-daily-planner"
      title="An ADHD daily planner built for real days"
      subtitle="A calm daily planner that helps you choose one thing that matters, take a few small wins, and pick the day back up when it slips."
    >
      <p>
        An ADHD daily planner is a simple planning tool shaped around how ADHD attention actually works, one clear
        priority first, then a few small wins, with room to restart when the day slips. Most planners assume a
        calm, predictable day. ADHD days are rarely like that. You start with good intentions, then a message
        pulls you sideways, a small task swells into three hours, and by the afternoon the plan reads like a list
        of things you already missed. CommandCenterADHD is a daily planner built for that reality. It helps you
        choose one thing that matters, collect a few small wins, and keep moving when the day changes shape.
      </p>

      <Section heading="Why most daily planners stop working for ADHD">
        <p>
          A long list looks organized, but it hides the one thing that actually matters today. When every item
          feels equal, your attention scatters and the hardest task quietly drifts to tomorrow. Rigid hourly
          schedules have the opposite problem. The moment real life breaks the timing, the whole grid feels wrong
          and you abandon it.
        </p>
        <p>
          A planner that works with ADHD needs to be gentle about priorities and forgiving about time. It should
          make the next step obvious and make it easy to start again after an interruption, because interruptions
          are the normal case, not the exception.
        </p>
      </Section>

      <Section heading="One priority, then a few quick wins">
        <p>
          Each day starts with a single question. What is the one thing worth protecting time for today. You write
          that down first, before anything else competes for space. Under it you keep a short set of quick wins,
          small finishes that give you momentum without draining you. The goal is progress you can feel, not a
          perfect record.
        </p>
        <p>
          If choosing the one thing is the hard part, the free{" "}
          <Link to="/what-should-i-do-next" className={a}>What should I do next tool</Link> can pick a starting
          point for you in a few seconds.
        </p>
      </Section>

      <Section heading="Honest time blocks instead of a rigid schedule">
        <p>
          Instead of locking every hour, you sketch a loose shape for the day. Something in the morning, something
          in the afternoon, a soft edge around the evening. If a block moves, you move it and nothing turns red.
          This keeps the plan useful when the day shifts, which with ADHD it usually will.
        </p>
      </Section>

      <Section heading="What a real day looks like">
        <p>
          Picture a Wednesday. You open the Today web app and set one priority, draft the project update and send
          it. Under it you add three quick wins, reply to Sam, refill your water, and book the dentist. You sketch
          two loose blocks, deep work in the morning and a team sync at two. By late morning the sync moves and a
          new request lands in your inbox. The old plan would feel ruined. Here you just move the block and keep
          going.
        </p>
        <p>
          After lunch the wheels come off and focus disappears. Instead of pushing, you switch on Low Battery Mode.
          The screen shrinks to one must do and one quick win, with a short focus timer. You start a five minute
          block, finish one small thing, and the day is moving again. Nothing was backfilled and nothing turned
          red.
        </p>
        <div className="grid gap-4 sm:grid-cols-2" data-testid="seo-today-screenshots">
          <figure className="overflow-hidden rounded-2xl border border-white/10 bg-white shadow-lg">
            <img
              src="/seo-today-daily.png"
              alt="The Today web app showing one priority, three quick wins, and two honest time blocks"
              width="430"
              height="760"
              loading="lazy"
              className="block h-auto w-full"
            />
          </figure>
          <figure className="overflow-hidden rounded-2xl border border-white/10 bg-white shadow-lg">
            <img
              src="/seo-today-lowbattery.png"
              alt="Low Battery Mode in the Today web app, showing one must do, one quick win, and a focus timer"
              width="430"
              height="760"
              loading="lazy"
              className="block h-auto w-full"
            />
          </figure>
        </div>
      </Section>

      <Section heading="Low Battery Mode for the hard days">
        <p>
          Some days you open the planner and everything feels like too much. For those days the Today web app has
          Low Battery Mode. You tell it how your brain feels, and if you are overwhelmed it shrinks the day down to
          what matters. You see one must do, one quick win, and a short focus timer to get moving. Everything else
          is tucked into a calm Can wait note you can open later. When you have more capacity, you return to the
          full plan in one tap. There is more about this on the <Link to="/adhd-overwhelm" className={a}>ADHD
          overwhelm page</Link>.
        </p>
      </Section>

      <Section heading="The printable planner and the full system">
        <p>
          There are two ways to use it. The <Link to="/free-planner" className={a}>free printable planner</Link>
          {" "}gives you the daily structure on paper, ready to use today. The full purchase includes two parts.
          The Notion Command Center is the planning system, and it works on the free Notion plan in any browser.
          The Today web app at commandcenteradhd.com/today is where you keep the daily view, quick wins, honest
          time blocks, and Low Battery Mode. You sign in to the Today web app with the email you used at checkout.
        </p>
      </Section>

      <Section heading="What it costs">
        <p>
          The Command Center is a one time purchase of $17. There is no subscription and no recurring charge, and
          future updates are included. If it does not fit the way you work, there is a 30 day money back guarantee.
          You can read more on the <Link to="/adhd-planner-no-subscription" className={a}>no subscription page</Link>.
        </p>
        <SeoCta slug="adhd-daily-planner" />
      </Section>

      <Section heading="Frequently asked questions">
        <SeoFaq faqs={SEO_FAQS["/adhd-daily-planner"]} />
      </Section>

      <SeoRelated
        links={[
          { to: "/adhd-overwhelm", label: "When ADHD overwhelm takes over, start here" },
          { to: "/adhd-planner-no-subscription", label: "An ADHD planner with no subscription" },
          { to: "/what-should-i-do-next", label: "Free tool: what should I do next?" },
        ]}
      />
    </PageShell>
  );
}
